import {
  ArrowRight,
  BadgeCheck,
  FileSpreadsheet,
  Landmark,
  PackageSearch,
  Receipt,
  Scale,
  SearchCheck,
  ShieldAlert,
} from "lucide-react";

import { TRDRFooter } from "@/components/layout/trdr-footer";
import { TRDRHeader } from "@/components/layout/trdr-header";
import { Button } from "@/components/ui/button";

const previewHref =
  "mailto:hello@trdrhub.com?subject=Duty%20Audit%20Private%20Preview";

const inputGroups = [
  {
    icon: FileSpreadsheet,
    title: "Customs history",
    body: "Declarations, broker reports, duty statements, and payment records.",
  },
  {
    icon: PackageSearch,
    title: "Product records",
    body: "Product masters, descriptions, classifications, rulings, and technical evidence.",
  },
  {
    icon: Receipt,
    title: "Commercial evidence",
    body: "Invoices, valuation support, origin evidence, freight, assists, and related-party data.",
  },
];

const workflow = [
  ["01", "Ingest", "Receive the agreed data extracts and supporting evidence."],
  ["02", "Normalize", "Map source fields into one customs-entry record while preserving the original values."],
  ["03", "Test", "Apply jurisdiction, tariff, classification, valuation, and origin checks."],
  ["04", "Investigate", "Review candidates against source evidence and resolve unsupported assumptions."],
  ["05", "Quantify", "Separate potential recovery, confirmed recovery, and exposure."],
  ["06", "Deliver", "Provide findings, evidence, action schedules, and recovery support."],
];

const outputs = [
  "Evidence-linked findings register",
  "Duty exposure and recovery schedule",
  "Classification, valuation, and origin analysis",
  "Correction and recovery action plan",
  "Audit record of sources, decisions, and review status",
];

export default function DutyAuditLanding() {
  return (
    <div className="min-h-screen bg-[#00261C]">
      <TRDRHeader />
      <main>
        <section className="relative overflow-hidden border-b border-[#EDF5F2]/10 pb-20 pt-36 md:pt-44">
          <div className="pointer-events-none absolute right-0 top-0 h-[540px] w-[540px] rounded-full bg-[#B2F273]/10 blur-[140px]" />
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(178,242,115,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(178,242,115,0.03)_1px,transparent_1px)] bg-[size:100px_100px] [mask-image:radial-gradient(ellipse_at_center,black_35%,transparent_75%)]" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[1.05fr_.95fr]">
              <div>
                <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-sky-300/25 bg-sky-300/10 px-4 py-2">
                  <BadgeCheck className="h-4 w-4 text-sky-200" />
                  <span className="font-mono text-xs uppercase tracking-[0.14em] text-sky-200">
                    Private Preview
                  </span>
                </div>
                <h1 className="font-display text-5xl font-bold tracking-tight text-white sm:text-6xl">
                  Duty Audit
                </h1>
                <p className="mt-5 max-w-2xl text-xl leading-relaxed text-[#EDF5F2]/70">
                  Find supported duty recovery opportunities and customs exposure across your
                  import history.
                </p>
                <p className="mt-5 max-w-2xl leading-relaxed text-[#EDF5F2]/50">
                  Enso normalizes customs records, tests historical treatment, investigates the
                  evidence, and produces an action-ready audit record. Recovery and exposure stay
                  separate throughout the engagement.
                </p>
                <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
                  <Button
                    asChild
                    size="lg"
                    className="h-auto border-none bg-[#B2F273] px-7 py-5 font-bold text-[#00261C] hover:bg-[#a3e662]"
                  >
                    <a href={previewHref}>
                      Request private preview
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                  <p className="text-sm text-[#EDF5F2]/60">
                    Launch scope from <strong className="text-white">$5,000</strong>
                  </p>
                </div>
              </div>

              <div className="rounded-2xl border border-[#B2F273]/20 bg-[#00382E]/70 p-7">
                <p className="font-mono text-xs uppercase tracking-[0.18em] text-[#B2F273]">
                  Audit record
                </p>
                <div className="mt-6 space-y-4">
                  <div className="rounded-xl border border-[#EDF5F2]/10 bg-[#00261C]/60 p-5">
                    <Landmark className="h-5 w-5 text-[#B2F273]" />
                    <h2 className="mt-3 font-display font-semibold text-white">
                      Declared treatment
                    </h2>
                    <p className="mt-1 text-sm leading-relaxed text-[#EDF5F2]/50">
                      Preserve the original code, origin, value, rates, duty, taxes, and fees.
                    </p>
                  </div>
                  <div className="rounded-xl border border-[#EDF5F2]/10 bg-[#00261C]/60 p-5">
                    <SearchCheck className="h-5 w-5 text-[#B2F273]" />
                    <h2 className="mt-3 font-display font-semibold text-white">
                      Reviewed finding
                    </h2>
                    <p className="mt-1 text-sm leading-relaxed text-[#EDF5F2]/50">
                      Link each finding to the rule, period, source, evidence, and review state.
                    </p>
                  </div>
                  <div className="rounded-xl border border-[#EDF5F2]/10 bg-[#00261C]/60 p-5">
                    <Scale className="h-5 w-5 text-[#B2F273]" />
                    <h2 className="mt-3 font-display font-semibold text-white">
                      Recovery or remediation
                    </h2>
                    <p className="mt-1 text-sm leading-relaxed text-[#EDF5F2]/50">
                      Track the action, authority response, realized recovery, or resolved exposure.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-[#EDF5F2]/10 py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-6xl">
              <div className="max-w-3xl">
                <p className="font-mono text-xs uppercase tracking-[0.18em] text-[#B2F273]">
                  Data in scope
                </p>
                <h2 className="mt-4 font-display text-3xl font-bold text-white sm:text-4xl">
                  Start with the records your customs team already uses.
                </h2>
              </div>
              <div className="mt-10 grid gap-5 md:grid-cols-3">
                {inputGroups.map(({ icon: Icon, title, body }) => (
                  <article
                    key={title}
                    className="rounded-2xl border border-[#EDF5F2]/10 bg-[#00382E]/45 p-6"
                  >
                    <Icon className="h-6 w-6 text-[#B2F273]" />
                    <h3 className="mt-5 font-display text-lg font-semibold text-white">{title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-[#EDF5F2]/50">{body}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-[#EDF5F2]/10 bg-[#EDF5F2] py-20 text-[#00261C]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[.7fr_1.3fr]">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.18em] text-[#315f50]">
                  Engagement workflow
                </p>
                <h2 className="mt-4 font-display text-3xl font-bold sm:text-4xl">
                  From raw customs data to supported action.
                </h2>
                <p className="mt-4 leading-relaxed text-[#00261C]/65">
                  Each stage preserves source values and records who made the decision.
                </p>
              </div>
              <ol className="grid gap-3 sm:grid-cols-2">
                {workflow.map(([number, title, body]) => (
                  <li key={number} className="rounded-xl border border-[#00261C]/10 bg-white/40 p-5">
                    <p className="font-mono text-xs font-bold text-[#315f50]">{number}</p>
                    <h3 className="mt-3 font-display font-semibold">{title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-[#00261C]/60">{body}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-2">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.18em] text-[#B2F273]">
                  Delivery
                </p>
                <h2 className="mt-4 font-display text-3xl font-bold text-white">
                  What your team receives
                </h2>
                <ul className="mt-7 space-y-3">
                  {outputs.map((output) => (
                    <li key={output} className="flex items-start gap-3 text-[#EDF5F2]/60">
                      <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#B2F273]" />
                      {output}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border border-[#B2F273]/20 bg-[#00382E]/60 p-8">
                <ShieldAlert className="h-6 w-6 text-[#B2F273]" />
                <p className="mt-5 font-mono text-xs uppercase tracking-[0.16em] text-[#B2F273]">
                  Founding engagement
                </p>
                <h2 className="mt-3 font-display text-3xl font-bold text-white">
                  Up to 1,000 customs entries
                </h2>
                <p className="mt-4 text-lg text-[#EDF5F2]/70">$5,000 audit fee</p>
                <p className="mt-2 text-lg text-[#EDF5F2]/70">
                  10% of duty actually recovered
                </p>
                <p className="mt-5 text-sm leading-relaxed text-[#EDF5F2]/50">
                  The recovery fee applies only after the customer receives the refund or legally
                  realizes the saving. Final scope depends on data quality, jurisdiction, and
                  evidence volume.
                </p>
                <Button
                  asChild
                  className="mt-7 w-full border-none bg-[#B2F273] font-bold text-[#00261C] hover:bg-[#a3e662]"
                >
                  <a href={previewHref}>Request private preview</a>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <TRDRFooter />
    </div>
  );
}
